import {Box} from '@mui/material';
import {FC, ReactNode, useCallback, useEffect, useState} from 'react';
import './ViewDocsDataGrid.scss';
import {GridSortModel} from '@mui/x-data-grid-pro';
import {t} from 'i18next';
import {DataGridRowSelectionModel} from '../../../../../../../shared/interfaces';
import ArrDataGridPro from '../../../../../../common/arr-data-grid-pro/ArrDataGridPro';
import ArrToastNotification from '../../../../../../common/arr-toast-notification/ArrToastNotification';
import DocPreviewModal from '../../../../../../document-view-prizmdoc/DocPreviewModal';
import {Columns} from './Column';
import ExpandedRow from './ExpandedRow';
import CellWrapper from '../../../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import ArrTooltip from '../../../../../../common/arr-tooltip/ArrTooltip';
import {
  useGetAIInvestigateDocumentSummary,
  useGetConversationDocumentDetails,
} from '../../../../../../../hooks/ai/investigate/useInvestigate';
import {ToastType} from '../../../../../../../shared/constants';
import {RootState} from '../../../../../../../redux/store';
import {useSelector} from 'react-redux';
export interface ViewDocsRow {
  documentId: number;
  docId: string;
  summary: string;
  similarityScore: number;
  [key: string]: string | number | boolean | null | undefined;
}

export interface DocForPreview {
  id: number;
  docId: string;
  docName: string;
  docExt?: string;
}

interface ViewDocsDataGridProps {
  transcriptId: number | string;
  indexId: number;
  conversationId: number | string;
  columnConfig?: any;
  selectedIds?: string[];
  unselectedIds?: string[];
  isAllSelected?: boolean;
  columnResize?: boolean;
  autoColumnSize?: boolean;
  onSelectionStateChange: (arg0: DataGridRowSelectionModel) => void;
  setEnableViewInDocListButton?: (val: boolean) => void;
}

const ROW_HEIGHT = 36;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ViewDocsDataGrid: FC<ViewDocsDataGridProps> = ({
  transcriptId,
  indexId,
  conversationId,
  onSelectionStateChange,
  setEnableViewInDocListButton,
  columnResize = false,
  isAllSelected,
  selectedIds,
  unselectedIds,
}) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'similarityScore', sort: 'desc'},
  ]);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedDocForPreview, setSelectedDocForPreview] =
    useState<DocForPreview | null>(null);
  const [summaryErrorToast, setSummaryErrorToast] = useState<{
    message: string;
  } | null>(null);

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [generatingRows, setGeneratingRows] = useState<number[]>([]);
  const [rows, setRows] = useState<ViewDocsRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const {mutateAsync: getDocumentSummary} =
    useGetAIInvestigateDocumentSummary();
  const userRights =
    useSelector((state: RootState) => state.actionmenu?.userRights) || {};
  /** Wrap cell with tooltip when needed */
  const wrap = useCallback((content: ReactNode, tooltip?: string) => {
    const cell = (
      <CellWrapper dataTestId={`cell-${tooltip}-datagrid`}>
        {content}
      </CellWrapper>
    );

    if (!tooltip?.trim()) return cell;

    return (
      <ArrTooltip
        trigger="hover"
        placement="top"
        content={tooltip}
        labelId={`cell-${tooltip}-${content}`}
      >
        {cell}
      </ArrTooltip>
    );
  }, []);

  /** Expand / Collapse details */
  const toggleExpand = (id: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const startGenerating = useCallback(
    async (id: number, docId: string) => {
      setGeneratingRows((prev) => (prev.includes(id) ? prev : [...prev, id]));

      try {
        const data = await getDocumentSummary({
          indexId,
          documentId: id,
          docId,
        });

        const documentId = data.documentId ?? id;

        setRows((prev) =>
          prev.map((row) =>
            row.documentId === documentId
              ? {...row, summary: data.summary}
              : row
          )
        );

        setExpandedRows((prev) => {
          const next = new Set(prev);
          next.add(documentId);
          return next;
        });
      } catch {
        setSummaryErrorToast({
          message: t('ai.investigate.viewInDocListGrid.summaryGenerationError'),
        });
      } finally {
        setGeneratingRows((prev) => prev.filter((x) => x !== id));
      }
    },
    [getDocumentSummary, indexId]
  );

  const handleSelectionStateChange = (data: DataGridRowSelectionModel) => {
    const selected = data.selectedIds;
    setEnableViewInDocListButton?.(selected.length > 0);
    onSelectionStateChange(data);
  };

  const {
    mutate: getConversationDocumentDetails,
    isPending: isDocumentDetailsLoading,
  } = useGetConversationDocumentDetails(
    (data) => {
      setTotalRows(data?.totalRecords ?? 0);
      const nextRows: ViewDocsRow[] =
        data?.documentDetails?.map((item) => ({
          documentId: item.documentId,
          docId: item.docId,
          summary: item.summary,
          similarityScore: item.similarityScore,
        })) ?? [];
      setRows(nextRows);
    },
    () => {
      setRows([]);
      setTotalRows(0);
    }
  );

  const getSortingColumn = (field?: string) => {
    if (field === 'similarityScore') return 'SimilarityScore';
    if (field === 'summary') return 'Summary';
    return 'DocId';
  };

  useEffect(() => {
    if (!transcriptId || !conversationId) {
      setRows([]);
      setTotalRows(0);
      return;
    }

    const primarySort = sortModel[0];
    getConversationDocumentDetails({
      transcriptId,
      conversationId,
      payload: {
        sortingColumn: getSortingColumn(primarySort?.field),
        direction: primarySort?.sort?.toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
        displayLength: pageSize,
        pageNumber,
      },
    });
  }, [
    transcriptId,
    conversationId,
    pageSize,
    pageNumber,
    sortModel,
    getConversationDocumentDetails,
  ]);

  const handleLinkClick = (documentId: number, docId: string) => {
    //Need to integrate in future, added mock data for now
    setSelectedDocForPreview({
      id: documentId,
      docId: docId,
      docName: 'test',
    });
  };

  return (
    <Box className="view-docs-data-grid">
      <ArrDataGridPro
        primaryKey="documentId"
        getRowId={(row: ViewDocsRow) => row.documentId}
        rowHeight={ROW_HEIGHT}
        loading={isDocumentDetailsLoading}
        className={`${DEFAULT_PAGE_SIZE > totalRows ? 'has-no-pagination' : ''}`}
        data-testid="view-docs-data-grid"
        columns={Columns(
          toggleExpand,
          startGenerating,
          expandedRows,
          generatingRows,
          wrap,
          handleLinkClick,
          userRights
        )}
        getDetailPanelContent={({row}) => (
          <ExpandedRow
            row={row}
            generating={generatingRows.includes(row.documentId)}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        detailPanelExpandedRowIds={expandedRows}
        onDetailPanelExpandedRowIdsChange={(ids) =>
          setExpandedRows(new Set(Array.from(ids) as number[]))
        }
        rows={rows}
        rowCount={totalRows}
        pagination
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        itemsPerPage={pageSize}
        setItemsPerPage={setPageSize}
        currentPage={pageNumber}
        onPageChange={setPageNumber}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          setPageNumber(1);
        }}
        disableColumnResize={!columnResize}
        enableCheckbox
        selectedIds={selectedIds}
        unselectedIds={unselectedIds}
        isAllSelected={isAllSelected}
        disableVirtualization
        onSelectionStateChange={handleSelectionStateChange}
        noRowsOverlayCustomText={t(
          'docExplorer.docDataGrid.noRowsOverlayCustomText'
        )}
        showPerPageOptions
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />

      {selectedDocForPreview && (
        <DocPreviewModal
          showModal
          onClose={() => setSelectedDocForPreview(null)}
          document={selectedDocForPreview}
        />
      )}

      {summaryErrorToast && (
        <ArrToastNotification
          message={summaryErrorToast.message}
          status={[ToastType.Error]}
          onClose={() => setSummaryErrorToast(null)}
        />
      )}
    </Box>
  );
};

export default ViewDocsDataGrid;
